import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import type { RouteObject } from 'react-router-dom'
import { Navigate, Outlet } from 'react-router-dom'

import Create from '@app/pages/metrics/create'
import List from '@app/pages/metrics/list'
import DataPointCreate from '@app/pages/metrics/metricDataPoints/create'
import DataPointEdit from '@app/pages/metrics/metricDataPoints/edit'
import Show from '@app/pages/metrics/show'
import Tabs from '@app/pages/metrics/tabs'
import MetricCorrelationsTab from '@app/pages/metrics/tabs/metricCorrelationsTab'
import MetricDataList from '@app/pages/metrics/tabs/metricDataList'
import MetricDetails from '@app/pages/metrics/tabs/metricDetails'
import MetricFormTab from '@app/pages/metrics/tabs/metricFormTab'
import credentialsRoutes from '@app/routes/credentials'
import { SharedEventRoutes } from '@app/routes/events'
import goalRoutes from '@app/routes/goals'
import metricSourceRoutes from '@app/routes/metricSources'
import CommentsFetcher from '@app/shared/comments/commentsFetcher'
import extractOrderings from '@app/shared/utils/extractOrderings'
import { useStore } from '@app/store'
import { requiresAuthorization } from '@app/utils/auth'
import { Metric, Metrics } from '@graphql/documents/metric.graphql'
import { MetricDataPoint } from '@graphql/documents/metric_data_point.graphql'
import type { MetricsQuery, MetricsQueryVariables } from '@graphql/queries'

const loadMetrics = async ({ request }) => {
  const url = new URL(request.url)
  const page = parseInt(url.searchParams.get('page'), 10) || 1
  const limit = parseInt(url.searchParams.get('limit'), 10) || null
  const filter = url.searchParams.get('filter')
  const strategyId = url.searchParams.get('strategyId')
  const order = extractOrderings(url.searchParams, ['name', 'metricDataPointsCount', 'sourceName'])
  const { loaderQuery } = useStore.getState()

  const variables: MetricsQueryVariables = omitBy({ page, limit, filter, order, strategyId }, isNull)

  const result = await loaderQuery<MetricsQuery, MetricsQueryVariables>(Metrics, variables)
  const { addObjectPage } = useStore.getState()

  const { collection = [], metadata } = result.data.metrics
  addObjectPage('metric', collection, metadata)

  return result.data
}

const loadMetric = async ({ params }) => {
  const { strategyId, nodeId } = params
  const { loaderQuery } = useStore.getState()

  loaderQuery(Metric, { id: nodeId, strategyId })

  return { metricId: nodeId }
}

const loadMetricDataPoint = async ({ params }) => {
  const { id } = params
  const { loaderQuery } = useStore.getState()

  const metricDataPointPromise = loaderQuery(MetricDataPoint, {
    metricDataPointId: id
  })

  return { metricDataPoint: metricDataPointPromise }
}

const metricShowTabRoutes = (namespace = null) => [
  {
    path: ':nodeId',
    children: [
      {
        path: '*',
        id: namespace ? `${namespace}-metric` : 'metric',
        loader: loadMetric,
        element: <Show />,
        children: [
          {
            index: true,
            element: <Navigate to="details" replace />
          },
          {
            element: <Tabs />,
            children: [
              {
                path: 'details',
                element: <MetricDetails />
              },
              {
                path: 'data',
                element: <MetricDataList />,
                children: [
                  {
                    path: 'new',
                    loader: async () => requiresAuthorization('create', 'metricDataPoint'),
                    element: <DataPointCreate />
                  },
                  {
                    path: ':id/edit',
                    loader: async (params) => {
                      await requiresAuthorization('update', 'metricDataPoint')

                      return loadMetricDataPoint(params)
                    },
                    element: <DataPointEdit />
                  }
                ]
              },
              {
                path: 'events',
                children: SharedEventRoutes('metric')
              },
              goalRoutes(namespace),
              metricSourceRoutes(namespace),
              {
                path: 'correlations',
                element: <MetricCorrelationsTab />
              },
              {
                path: 'comments',
                element: <CommentsFetcher commentableType="metric" />
              },
              {
                path: 'settings',
                loader: async () => requiresAuthorization('update', 'metric'),
                element: <MetricFormTab />
              }
            ]
          }
        ]
      }
    ]
  }
]

const routes: RouteObject = {
  path: 'metrics',
  element: <Outlet />,
  children: [
    {
      index: true,
      loader: loadMetrics,
      element: <List />
    },
    {
      path: 'new',
      loader: async () => requiresAuthorization('create', 'metric'),
      element: <Create />
    },
    credentialsRoutes,
    ...metricShowTabRoutes()
  ]
}
export { metricShowTabRoutes, Show }

export default routes
